import type { Locale } from "./constants.js";

import enCA from "./locales/en-CA/timezones.json" with { type: "json" };
import enUS from "./locales/en-US/timezones.json" with { type: "json" };
import frCA from "./locales/fr-CA/timezones.json" with { type: "json" };
import esMX from "./locales/es-MX/timezones.json" with { type: "json" };
import jaJP from "./locales/ja-JP/timezones.json" with { type: "json" };
import ptPT from "./locales/pt-PT/timezones.json" with { type: "json" };
import paIN from "./locales/pa-IN/timezones.json" with { type: "json" };

// CLDR's per-locale composition patterns, `{0}`/`{1}` placeholders and all. `regionFormat` names a
// zone from its region, `fallbackFormat` is the locale's way of joining a zone name and a city,
// `gmtFormat`/`gmtZeroFormat` render an offset. No zone *name* is stored: four of the six ECMA-402
// name styles change with DST, and ICU already produces all six from the runtime's own data.
export type TimezonePatterns = {
    regionFormat: string;
    fallbackFormat: string;
    gmtFormat: string;
    gmtZeroFormat: string;
};

// The default locale's file is the zone list — nothing else enumerates the zones — so its keys define the
// id union, and `tsc` rejects any other locale's file that is missing one of them. A stray id, a blank
// city and a reordered file are past what the type can see; `timezoneCatalogs.test.ts` covers those.
export type TimezoneId = keyof (typeof enCA)["zone"];

// One localized exemplar city per zone, nested under `zone` so the i18next key is `zone.<IANA id>`.
export type TimezonesCatalog = { zone: Record<TimezoneId, string>; patterns: TimezonePatterns };

export const timezoneCatalogs: Record<Locale, TimezonesCatalog> = {
    "en-CA": enCA,
    "en-US": enUS,
    "fr-CA": frCA,
    "es-MX": esMX,
    "ja-JP": jaJP,
    "pt-PT": ptPT,
    "pa-IN": paIN,
};

/** Every IANA zone id the catalogs carry, in the generator's sorted order. */
export const TIMEZONE_IDS: readonly TimezoneId[] = Object.keys(enCA.zone) as TimezoneId[];
